import cloneDeep from "lodash/cloneDeep";
import { List, Map } from "immutable";
import {
    IIdentifierFields,
    INewUpcsByProductId,
    IUPCData,
    IUPCDataFromUpload,
    ProductDataMap,
    ProductList,
} from "helpers/validators/import-UPC-validator/types";
import { UPC_COLUMN_FIELD, SIZE_COLUMN_FIELD } from "./constants";
import { FilterValueField } from "containers/sites-manager/hooks";
import { PIPE_CHARACTER_REGEX, Item, ItemFields, ITEMS_ID } from "constants/product-collection";
import { IMutateCollectionItemPayload } from "actions/collections";

export const getDisplayNamesForIdentifierField = (
    identifierFields: string[],
    schema: List<Map<string, string>>
): IIdentifierFields[] => {
    const schemaLookup = schema?.reduce(
        (map, item) => map.set(item.get("field_name"), item.get("display_name")),
        Map<string, string>()
    );

    const displayNames: IIdentifierFields[] = [];
    for (const key of identifierFields) {
        const display_name = schemaLookup.get(key);

        if (display_name !== undefined) {
            displayNames.push({ key, display_name });
        }
    }

    return displayNames;
};

export const parseUPCData = (upcData: IUPCDataFromUpload[], schema: List<Map<string, string>>): IUPCData[] => {
    const sizeColumnField = SIZE_COLUMN_FIELD.display_name.toLowerCase();
    const upcColumnField = UPC_COLUMN_FIELD.display_name.toLowerCase();
    const result: IUPCData[] = [];
    const schemaLookup = schema.reduce(
        (map, item) =>
            map.set(item.get("display_name").toLowerCase(), {
                field_name: item.get("field_name"),
                display_name: item.get("display_name"),
            }),
        Map<string, { field_name: string; display_name: string }>()
    );

    for (const data of upcData) {
        const parsedData: IUPCData = {};

        // Check if all values in the data object are empty strings
        if (Object.values(data).every((val) => val === "")) {
            continue; // Skip this data object and move on to the next one
        }

        for (const [key, value] of Object.entries(data)) {
            const field_name = key;
            const field_name_lowerCase = field_name.toLowerCase();
            const { field_name: parsedKey, display_name } = schemaLookup.get(field_name_lowerCase) ?? {};

            switch (field_name_lowerCase) {
                case sizeColumnField:
                    parsedData[SIZE_COLUMN_FIELD.key] = {
                        display_name: SIZE_COLUMN_FIELD.display_name,
                        value,
                    };
                    break;
                case upcColumnField:
                    parsedData[UPC_COLUMN_FIELD.key] = {
                        display_name: UPC_COLUMN_FIELD.display_name,
                        value,
                    };
                    break;
                default:
                    if (parsedKey) {
                        parsedData[parsedKey] = { display_name, value };
                    }
            }
        }

        result.push(parsedData);
    }

    return result;
};

const getSize = (row: IUPCData): string => row[SIZE_COLUMN_FIELD.key].value.toString().toLowerCase();

const getRowIdentifiers = (idFields: string[], row: IUPCData): Record<string, string> =>
    idFields.reduce((acc, identifier) => {
        const rowValue = row[identifier]?.value.toString().toLowerCase();
        if (rowValue) {
            acc[identifier] = rowValue;
        }
        return acc;
    }, {});

const productMatchWithIdentifiers = (
    productToUpdate: ProductDataMap,
    rowIdentifiers: Record<string, string>
): boolean =>
    Object.entries(rowIdentifiers).every(
        ([key, value]) =>
            productToUpdate.get(ItemFields.Fields).get(key).get(FilterValueField.String)?.toString()?.toLowerCase() ===
            value
    );

const getUpcs = (
    productId: string,
    productToUpdate: ProductDataMap,
    newUpcsByProductId: INewUpcsByProductId
): string[] =>
    (newUpcsByProductId[productId]
        ? newUpcsByProductId[productId]
        : (productToUpdate.get(Item.FIELDS.UPC[0])?.get(Item.FIELDS.UPC[1])?.get(Item.FIELDS.UPC[2]) ?? "")
    ).split(",");

export const getNewUpcsByProductId = (
    idFields: string[] = [],
    parsedUpcData: IUPCData[] = [],
    products: ProductList
): INewUpcsByProductId => {
    const currentProducts = cloneDeep(products ?? []);
    const newUpcsByProductId = {};

    parsedUpcData.forEach((row) => {
        const size = getSize(row);
        const rowIdentifiers = getRowIdentifiers(idFields, row);

        if (Object.keys(rowIdentifiers).length === idFields.length) {
            currentProducts.forEach((productToUpdate) => {
                const productId = productToUpdate.getIn(Item.FIELDS.PRODUCT_ID);
                const hasProductId = Boolean(productId);

                const manufacturedSizeField = productToUpdate.getIn(Item.FIELDS.NU_SIZES_MANUFACTURED);
                const selectedSizeField = productToUpdate.getIn(Item.FIELDS.SIZES_SELECTED);

                const productSizes = hasProductId
                    ? sanitizeAndSplitString(manufacturedSizeField)
                    : sanitizeAndSplitString(selectedSizeField, PIPE_CHARACTER_REGEX);
                const productSizePosition = productSizes?.indexOf(size);

                if (productMatchWithIdentifiers(productToUpdate, rowIdentifiers) && productSizePosition > -1) {
                    const id = productToUpdate.getIn(Item.FIELDS.ID);
                    const upcs = getUpcs(id, productToUpdate, newUpcsByProductId);

                    upcs[productSizePosition] = row[UPC_COLUMN_FIELD.key]?.value;
                    newUpcsByProductId[id] = upcs.join(",");
                }
            });
        }
    });

    return newUpcsByProductId;
};

export const mutateProductsWithNewUpc = (
    newUpcsByProductId: INewUpcsByProductId,
    collectionItems,
    onMutateCollectionItem: (params: IMutateCollectionItemPayload) => void
) => {
    Object.keys(newUpcsByProductId).forEach((productId) => {
        const newUpcs = newUpcsByProductId[productId];
        const rowIndex =
            collectionItems &&
            collectionItems.findIndex((collectionItem) => collectionItem.getIn(Item.FIELDS.ID) === productId);
        let updatedRow = collectionItems && collectionItems.get(rowIndex);

        if (updatedRow) {
            updatedRow = updatedRow.setIn(Item.FIELDS.UPC, newUpcs);
            onMutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow });
        }
    });
};

export const getSanitizedSize = (field, regex?: RegExp) => {
    const fieldString = field?.get(FilterValueField.String);
    return sanitizeAndSplitString(fieldString, regex);
};

export const sanitizeAndSplitString = (inputString: string | undefined, regex?: RegExp): string[] => {
    if (!inputString) {
        return [];
    }

    const sanitizedInput = regex ? inputString.toLowerCase().replace(regex, "") : inputString.toLowerCase();

    return sanitizedInput.split(",");
};
